import { useMutation, useQueryClient } from '@tanstack/react-query';
import { maxUint256 } from 'viem';
import { useWalletClient } from 'wagmi';
import { ERC20_ABI } from '../config/oft';
import { getCctpConfig } from '../config/cctp';

/**
 * Returns a mutation function to approve USDC for the CCTP TokenMessengerV2
 * on the specified chain.
 *
 * Approves maxUint256 so future burns don't require re-approval.
 * USDC does not have the USDT non-zero reset issue, so no reset step is needed.
 *
 * @param chainId - EVM chain ID of the chain where the burn will happen
 */
export function useCctpApprove(chainId: number | undefined) {
  const { data: walletClient } = useWalletClient({ chainId });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!chainId) {
        throw new Error('chainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const cctpConfig = getCctpConfig(chainId);

      const txHash = await walletClient.writeContract({
        address: cctpConfig.usdcAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [cctpConfig.tokenMessenger, maxUint256],
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return txHash;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['cctpAllowance', chainId],
      });
    },
  });
}
